import type {
  ActivityEntry,
  ActivitySummary,
  CreateStaff,
  PageInfo,
  Staff,
  StaffRole,
  UpdateStaff,
} from '@atlas-control/shared';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, buildUrl } from '@/lib/api';

export const staffQuery = queryOptions({
  queryKey: ['staff'],
  queryFn: () => api<{ staff: Staff[] }>('/staff').then((r) => r.staff),
});

export function useStaffMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: staffQuery.queryKey });

  return {
    create: useMutation({
      mutationFn: (body: CreateStaff) => api<{ staff: Staff }>('/staff', { method: 'POST', body }),
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({ id, ...body }: UpdateStaff & { id: string }) =>
        api<{ staff: Staff }>(`/staff/${id}`, { method: 'PATCH', body }),
      onSuccess: refresh,
    }),
    setStatus: useMutation({
      mutationFn: ({ id, status }: { id: string; status: 'active' | 'disabled' }) =>
        api<{ staff: Staff }>(`/staff/${id}/${status === 'active' ? 'enable' : 'disable'}`, {
          method: 'POST',
        }),
      onSuccess: refresh,
    }),
    resetPassword: useMutation({
      mutationFn: (id: string) => api(`/staff/${id}/reset-password`, { method: 'POST' }),
    }),
  };
}

export interface ActivityFilters {
  actorRole?: StaffRole;
  actorId?: string;
  action?: string;
  from?: string;
  to?: string;
}

export const activityQuery = (filters: ActivityFilters, page: number) =>
  queryOptions({
    queryKey: ['activity', filters, page],
    queryFn: () =>
      api<{ entries: ActivityEntry[] } & PageInfo>('/activity', {
        query: { ...filters, page },
      }),
    placeholderData: keepPreviousData,
  });

export const activitySummaryQuery = queryOptions({
  queryKey: ['activity', 'summary'],
  queryFn: () => api<{ staff: ActivitySummary[] }>('/activity/summary').then((r) => r.staff),
});

export function activityCsvUrl(filters: ActivityFilters): string {
  return buildUrl('/activity/export.csv', { ...filters });
}
